import { existsFilter, getRejectionBucketingFilter } from '@iotv/core-services'
import {
  AdjacentType,
  DatastoreObjectType,
  DatastoreObjectTypes,
  EdgeInstanceType,
  EdgeParams,
  getObjectHash,
  GraphStep,
  HydrateableSkMappedItems,
  isEdge,
  isNode,
  isPseudoAttribute,
  isValidBusinessObject,
  MapLike,
  NodeEdgemapperLike,
  objectsMatch,
  OptionalQueryHashes,
  PKMappedItems,
  SkMappedItems
} from '@iotv/iotv-v3-types'
import { IBranchingQueryDefinition } from '../../../util/dynamodb/GraphQuery/BranchingQueryDefinition'
import { applyNodeFilter } from '../../../util/dynamodb/NodeListFilter'
import { DEBUG_OVERRIDE, IS_FARGATE, NOT_LAMBDA } from '../../../util/lambda'
import { reKeyItem, summarizeCoreApplicationObjects } from '../../../util/objects/identity'
import { getMinimalVOB } from '../../../util/TypeHelpers'
import { enumerable } from '../enumerableDecorator'
import { invertSkMapped } from './NodeEdgeMapperUtils'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export type NodeMapperIterationRequest<TS extends DatastoreObjectTypes> = {
  fn: (items: TS) => TS
  items: TS
  maxIterations?: number
  iteration?: number
}

export type MissingNodeAndEdges = { nodes: DatastoreObjectTypes; edges: DatastoreObjectTypes }

/**
 * @class NodeEdgeMapper provides double indexed store of nodes and edges to facilitate hierachy and aggregation functions
 * NodeEdgeMapper must hold contract that pkMapped[sk] === skMapped[pk] ie objects are referenced in maps.
 */
export class NodeEdgeMapper<T extends DatastoreObjectType> implements NodeEdgemapperLike<T> {
  /** TODO this must be refactored as objects references are not correct, must construct from SK with PK being referenced from SK */
  constructor(nodeEdgeMapperZombie?: HydrateableSkMappedItems<T> & OptionalQueryHashes) {
    if (nodeEdgeMapperZombie) {
      this.skMapped = nodeEdgeMapperZombie.skMapped

      this.pkMapped = invertSkMapped(nodeEdgeMapperZombie.skMapped)

      this.queryHashes = nodeEdgeMapperZombie.queryHashes
      this.nodeCount = this.countNodes()
      this.edgeCount = this.getContainedEdges().length
    } else {
      this.nodeCount = 0
      this.edgeCount = 0
    }
  }

  skMapped: SkMappedItems<T> = {}
  pkMapped: PKMappedItems<T> = {}

  /** node count provides an as-added/removed count in this. It in theory be the same as countNodes */
  nodeCount: number
  /** edge count provides an as-added/removed count in this. */
  edgeCount: number

  /** queryHashes is convenience place for use when avoiding queries after hydration when they have already been made, user descretion adviced, there is no tooling to verify whether these make sense */
  queryHashes?: string[]

  /** returns a hash of this */
  @enumerable(false)
  getHash = () => getObjectHash(this)

  /** axisNode item used to idenfify a single item from which other items are referenced ( like a star graph ), as opposed to apexNodes which are nodes from which other items descend */
  axisNode: T | undefined = undefined
  @enumerable(false)
  setAxisNode = (item: T) => (this.axisNode = item)

  @enumerable(false)
  getAxisNode = () => this.axisNode

  @enumerable(false)
  extractAxisNodeEdgeMapper = (): NodeEdgeMapper<T> => this.extractSingleNodeEdgeMapper(this.axisNode)

  @enumerable(false)

  /** creates a nodeEdgeMapper with the adjacent nodes and edges to context object */
  extractSingleNodeEdgeMapper = (node: T | undefined): NodeEdgeMapper<T> => {
    const axisNEM = new NodeEdgeMapper<T>()
    if (node) {
      axisNEM.loadItems([node, ...this.getChildEdges(node), ...this.getChildNodes(node), ...this.getParentEdges(node), ...this.getParentNodes(node)])
    }
    return axisNEM
  }

  /** simple Object.assign of dehydrahed NodeEdgeMapper without counts will result in bad count of zero */
  /** returns item if exists if not undefined */
  @enumerable(false)
  getItem = (item: T): T | undefined => this.skMapped[item.sk]?.[item.pk]

  /** loads a single edge or node, affects count */
  @enumerable(false)
  loadItem = (item: T): void => {
    if (!this.pkMapped[item.pk]) {
      this.pkMapped[item.pk] = {}
    }
    this.pkMapped[item.pk][item.sk] = item

    if (!this.skMapped[item.sk]) {
      this.skMapped[item.sk] = {}
    }

    if (isNode(item) && !this.skMapped[item.sk][item.pk]) {
      this.nodeCount++
    }

    if (isEdge(item) && !this.skMapped[item.sk][item.pk]) {
      this.edgeCount++
    }

    this.skMapped[item.sk][item.pk] = item
  }

  /** updates or creates item */
  @enumerable(false)
  upsert = (item: T): void => {
    this.loadItem({ ...this.getItem(item), ...item })
  }

  /**
   * Removes item from nodeEdgeMapper, affects count, if the item is a node, all the edges are removed as well. If the item is an edge, only that edge is removed
   */
  @enumerable(false)
  removeItem = (item: T): T[] => {
    let removedItems: T[] = []
    if (this.itemExists(item)) {
      if (isNode(item)) {
        const edges = [...this.getChildEdges(item), ...this.getParentEdges(item)]
        for (let edge of edges) {
          removedItems.push(...this.removeItem(edge))
        }
      }
      delete this.skMapped[item.sk][item.pk]
      delete this.pkMapped[item.pk][item.sk]
      removedItems.push(item)

      if (Object.keys(this.skMapped[item.sk]).length === 0) {
        delete this.skMapped[item.sk]
      }
      if (Object.keys(this.pkMapped[item.pk]).length === 0) {
        delete this.pkMapped[item.pk]
      }

      if (isNode(item)) {
        this.nodeCount--
      }

      if (isEdge(item)) {
        this.edgeCount--
      }
    }
    return removedItems
  }

  @enumerable(false)
  itemNotUndefined = <T>(item: T) => item

  /** Load Items into Node Edge Mapper
   * @param {T[]} items - list of items to add
   */
  @enumerable(false)
  loadItems = (items: T[]): void => items.filter(existsFilter).forEach(this.loadItem)

  @enumerable(false)
  upsertItems = (items: T[]): void => items.forEach(this.upsert)

  /** gets union, ie merges with another nodeEdgeMapper, mutates but does not return thiss */
  @enumerable(false)
  union = (nodeEdgeMapper: NodeEdgeMapper<T>): void => {
    const items = nodeEdgeMapper.getContainedNodesAndEdges()
    this.loadItems(items)
  }

  @enumerable(false)
  getPrimaryItem = (sk: string): T | undefined => this.skMapped[sk]?.[sk]

  /**
   * Get all of the primaryItems
   */
  @enumerable(false)
  getContainedItems = (): (T & { type?: string })[] =>
    Object.keys(this.skMapped).map(this.getPrimaryItem).filter(this.itemNotUndefined) as (T & { type?: string })[]

  /**
   *  Get all the parentEdges for each primaryItem - slow for large collections
   */
  @enumerable(false)
  getContainedEdgesSlow = () =>
    this.getContainedItems()
      .map(this.getParentEdges)
      .reduce((a, c) => {
        c.forEach((cx) => {
          if (!a.find((ax) => ax.sk === cx.sk && ax.pk === cx.pk)) {
            a.push(cx)
          }
        })
        return a
      }, [] as T[])

  /** gets target node from source node and edge, and vice versa */
  @enumerable(false)
  getOtherNode = (firstNode: T, edge: T) => {
    let otherNodeSk: string = 'never'
    if ([edge.sk, edge.pk].includes(firstNode.sk)) {
      if (firstNode.sk === edge.sk) {
        otherNodeSk = edge.pk
      } else if (firstNode.sk === edge.pk) {
        otherNodeSk = edge.sk
      }
    }
    return this.skMapped[otherNodeSk]?.[otherNodeSk]
  }

  @enumerable(false)
  getNodesByPath = (source: T, path: GraphStep[]) => {
    let sourceNodes = [source]
    for (let step of path) {
      sourceNodes = this.getAdjacentNodesFromMultiSource(sourceNodes, step)
    }

    return sourceNodes
  }

  /** Execute a path query on this NodeEdgeMapper contents returning all nodes and edges in the path from items, including filter support
   * This is potentially replaced by getTraversal ( note getTraversal takes single item )
   */
  @enumerable(false)
  queryByGraphSteps = (items: T[], queryPath: EdgeParams[]) => {
    const resultNodeEdgeMapper = new NodeEdgeMapper<T>()
    const queriedItems = queryPath.reduce((a, pathstep) => {
      const iterationARes = a.reduce((b, item) => {
        const { nodes, edges } = this.getAdjacentNodesAndEdges(item, pathstep)
        /** very large collections will error Array.push with  Maximum call stack size exceeded */
        try {
          if (nodes.length < 50000) {
            b.push(...nodes, ...edges)
          } else {
            nodes.forEach((node) => b.push(node))
          }
        } catch (e: any) {
          console.log(`queryByGraphSteps iterationARes reduce failed with ${nodes.length} nodes ${edges.length} edges with: ${e.message}`)
        }

        return b
      }, [] as T[])

      try {
        if (iterationARes.length < 50000) {
          a.push(...iterationARes)
        } else {
          iterationARes.forEach((node) => a.push(node))
        }
      } catch (e: any) {
        console.log(`queryByGraphSteps queriedItems reduce failed with ${iterationARes.length} items with: ${e.message}`)
      }
      a.push(...iterationARes)
      return a
    }, items as T[])
    resultNodeEdgeMapper.loadItems(queriedItems)
    return resultNodeEdgeMapper
  }

  /** Gets adjancent edges selected by adjacenyType, objectTypeId and edgeTypeId. This DOES NOT INCLUDE the initial item and will not return pseudo attributes ( like DeviceConfig ) */
  @enumerable(false)
  getAdjacentEdges = (item: T, edgeParams: EdgeParams) => {
    const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams
    let allEdges: (T & EdgeInstanceType)[] = []
    if (adjacencyType === AdjacentType.CHILD) {
      allEdges = this.getChildEdges(item)
    } else if (adjacencyType === AdjacentType.PARENT) {
      allEdges = this.getParentEdges(item)
    }
    const edges = allEdges.filter((edge) => !edgeTypeId || edge.edgeTypeId === edgeTypeId)
    return edges
  }

  /** returns pseudo attributes like DeviceConfig */
  @enumerable(false)
  getPseudoAttributes = (item: T, type?: string) => {
    return Object.entries(this.skMapped)
      .reduce((A, [K, C]) => {
        A.push(
          ...Object.entries(C).reduce((a, c) => {
            const [k, possibleEdge] = c
            if (k === item.sk && isEdge(possibleEdge)) {
              const otherNode = this.getOtherNode(item, possibleEdge)
              if (!otherNode) {
                a.push(possibleEdge)
              }
              debug && console.log(k, `other node`, getMinimalVOB(possibleEdge), otherNode)
            }
            return a
          }, [] as T[]),
        )
        return A
      }, [] as T[])
      .filter((item) => !type || (item as T & { type: string }).type === type)
  }

  /** returns all pseudoAttributes */
  @enumerable(false)
  getContainedPseudoAttributes = () => Object.values(this.skMapped).reduce( ( a, c  ) => {
      const y = Object.values( c ).filter( existsFilter).filter( isPseudoAttribute )
      a.push( ...y)
      return a
    }, [] as T[])


  /** Gets adjancent nodes and edges selected by adjacenyType, objectTypeId and edgeTypeId. This DOES NOT INCLUDE the initial item. initial use in UI to select from applicationData */
  @enumerable(false)
  getAdjacentNodesAndEdges = (item: T, edgeParams: EdgeParams): { nodes: T[]; edges: T[] } => {
    const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams
    let adjacentEdges: (T & EdgeInstanceType)[] = this.getAdjacentEdges(item, edgeParams)
    const edgeNodes = adjacentEdges
      .map((edge) => [edge, this.getOtherNode(item, edge)]) // this probably should/could have a predicate on edgeTypeId here
      .filter(([edge, node]) => (node as T & { type: string })?.type === objectTypeId)

    let nodes: T[] = []
    let edges: T[] = []

    edgeNodes.forEach(([edge, node]) => {
      edges.push(edge as T), nodes.push(node as T)
    })

    if (edgeParams.nodeFilters) {
      const { filteredNodes, nodeFilteredEdges } = applyNodeFilter({ nodeFilterGroups: edgeParams.nodeFilters, nodes, edges })
      nodes = filteredNodes as T[]
      edges = nodeFilteredEdges as T[]
    }

    return { nodes, edges }
  }

  /** @Beta ( 2024 04 - used in UI customer selector) gets all nodes and edges from multi step traversal, returns NodeEdgeMapper with traversal including source item */
  @enumerable(false)
  getTravesal = (item: T, queryPath: EdgeParams[]): NodeEdgeMapper<T> => {
    const nem: NodeEdgeMapper<T> = new NodeEdgeMapper<T>()
    nem.loadItem(item)
    queryPath.reduce(
      (lastNodes, edgeParams) => {
        const nextNodes: T[] = []
        lastNodes.forEach((n) => {
          const { nodes, edges } = this.getAdjacentNodesAndEdges(n, edgeParams)
          nem.loadItems([...nodes, ...edges])
          nodes.forEach((nx) => {
            if (!nextNodes.find((ny) => objectsMatch(ny, nx))) {
              nextNodes.push(nx)
            }
          })
        })
        return nextNodes
      },
      [item],
    )
    return nem
  }

  /** applies branching query definition to this nodeEdgeMapper
   * tests for this are at file:///./../../../../../../Utilities/test/TestData/getNestedEntityGroups.test.ts
   * and file:///./../../../../../../iotv/datastore/test/GraphQuery/Groups/Descending/groupHierarchyDescending.test.ts
   * If the nodeEdgeMapper is populated from a branching query definition, applying the same definition should give exactly the same result
   * @param items - The item to start the query from. Recursive calls pass all items, initial call should just be context item
   * @param results - not usually supplied by consumer, it is populated in recursion
   */
  @enumerable(false)
  applyBranchingQuery = (items: T[], query: IBranchingQueryDefinition, results?: NodeEdgeMapper<T>): NodeEdgeMapper<T> => {
    if (!results) {
      // on first call setup results and add itesm
      results = new NodeEdgeMapper<T>()
      results.loadItems(items)
    }
    const { path, subQueries } = query
    const thisStepResults = path.reduce((lastNodes, edgeParams) => {
      debug && console.log(`edgeParams`, edgeParams)
      const nextNodes: T[] = []
      /** special case where query has SUBJECT, eg in apex query */
      if (edgeParams.adjacencyType === AdjacentType.SUBJECT) {
        nextNodes.push(...lastNodes)
      } else {
        lastNodes.forEach((n) => {
          const { nodes, edges } = this.getAdjacentNodesAndEdges(n, edgeParams)
          debug && console.log(`adj res`, nodes)
          results!.loadItems([...nodes, ...edges])
          nodes.forEach((nx) => {
            if (!nextNodes.find((ny) => objectsMatch(ny, nx))) {
              nextNodes.push(nx)
            }
          })
        })
      }

      return nextNodes
    }, items)

    if (thisStepResults.length > 0) {
      for (let subQuery of subQueries) {
        results.union(this.applyBranchingQuery(thisStepResults, subQuery))
      }
    }

    return results
  }

  /**
   * Gets adjacent nodes, selected by {@link AdjacentType}, `objectTypeId` and `edgeTypeId`
   *
   * This **DOES NOT INCLUDE** the initial item */
  @enumerable(false)
  getAdjacentNodes = (item: T, edgeParams: EdgeParams) => {
    const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams

    const adjacentEdges: (T & EdgeInstanceType)[] = this.getAdjacentEdges(item, edgeParams)
    const edgeNodePairs = adjacentEdges
      .map((edge) => [edge, this.getOtherNode(item, edge)])
      .filter(([edge, node]) => objectTypeId === '*' || (node as T & { type: string })?.type === objectTypeId)

    let nodes: T[] = edgeNodePairs.filter(([edge, node]) => node !== undefined).map(([edge, node]) => node) as T[]
    if (edgeParams.nodeFilters) {
      const { filteredNodes } = applyNodeFilter({ nodeFilterGroups: edgeParams.nodeFilters, edges: [], nodes })
      nodes = filteredNodes as T[]
    }
    return nodes
  }

  @enumerable(false)
  getAdjacentNodesFromMultiSource = (items: T[], edgeParams: EdgeParams) => {
    let nodes: T[] = []
    for (let item of items) {
      nodes = nodes.concat(this.getAdjacentNodes(item, edgeParams))
    }
    return nodes
  }

  /** used by get contained edges, this does not get child edges for an item */
  @enumerable(false)
  getEdgesForItem = (sk: string): T[] =>
    this.skMapped[sk]
      ? Object.entries(this.skMapped[sk]).reduce((a, [pk, ob]) => {
          if (pk !== sk) a.push(ob as T)
          return a
        }, [] as T[])
      : []

  /**
   *  Get all the parentEdges for each primaryItem ( 15x faster )
   */
  @enumerable(false)
  getContainedEdges = (): T[] =>
    Object.keys(this.skMapped).reduce((a, c) => {
      a = [...a, ...this.getEdgesForItem(c)]
      return a
    }, [] as T[])

  /** get edge between items */
  @enumerable(false)
  getEdgeBetween = (itemA: T, itemB: T) => {
    let edge: T | null = null
    if (itemA.sk !== itemB.sk) {
      const canonicalOut = this.skMapped[itemA.sk]?.[itemB.sk]
      const canonicalIn = this.skMapped[itemB.sk]?.[itemA.sk]
      if (canonicalIn && canonicalOut) {
        !IS_FARGATE && console.log(`WARN getEdgeBetween interconected nodes`, { itemA: itemA.sk, itemB: itemB.sk, canonicalOut, canonicalIn })
      } else if (!canonicalIn && !canonicalOut) {
        !IS_FARGATE && console.log(`WARN getEdgeBetween no edge between`, { itemA: itemA.sk, itemB: itemB.sk, edges: this.getEdgesForItem(itemA.sk) })
      } else {
        debug && console.log(`getEdgeBetween`, { itemA, itemB, canonicalOut, canonicalIn })
      }
      edge = canonicalOut ?? canonicalIn ?? null
    }
    return edge
  }

  /**
   * Get all nodes and edges
   */
  @enumerable(false)
  getContainedNodesAndEdges = () => [...this.getContainedItems(), ...this.getContainedEdges()]

  @enumerable(false)
  isApexNode = (item: T): boolean => this.skMapped[item.sk] && Object.keys(this.skMapped[item.sk]).length === 1 && this.skMapped[item.sk][item.sk] !== undefined

  /** gets all nodes with only children */
  @enumerable(false)
  getApexNodes = (): T[] => this.getContainedItems().filter(this.isApexNode)

  /** handing node is one with no child related items */
  @enumerable(false)
  isHangingNode = (item: T): boolean =>
    this.pkMapped[item.sk] && Object.keys(this.pkMapped[item.sk]).length === 1 && this.pkMapped[item.sk][item.sk] !== undefined

  /** gets all handing nodes */
  @enumerable(false)
  getHangingNodes = (): T[] => this.getContainedItems().filter(this.isHangingNode)

  /** test whether item is an edge and has missing source or target nodes */
  @enumerable(false)
  isUnterminatedEdge = (item: T): item is EdgeInstanceType & T =>
    this.skMapped[item.sk][item.pk] !== undefined && (!this.skMapped[item.pk]?.[item.pk] || !this.skMapped[item.sk]?.[item.sk])

  /** gets all unterminated edges, either no source or target item exists in this*/
  @enumerable(false)
  getUnterminatedEdges = (): (EdgeInstanceType & T)[] => this.getContainedEdges().filter(this.isUnterminatedEdge)

  /** tests wether node is not connected to any other node in the collection */
  @enumerable(false)
  isOrphanNode = (item: T): boolean => this.isHangingNode(item) && this.isApexNode(item)

  /** returns nodes not connected to any other node in the collection */
  @enumerable(false)
  getOrphanedNodes = (): T[] => this.getContainedItems().filter(this.isOrphanNode)

  @enumerable(false)
  getChildEdges = (item: T): (T & EdgeInstanceType)[] =>
    Object.values(this.pkMapped[item.sk] ?? {}).filter((child) => child && child.sk !== item.sk) as (T & EdgeInstanceType)[]

  @enumerable(false)
  getChildNodes = (item: T): T[] =>
    this.getChildEdges(item)
      .map((item) => this.getPrimaryItem(item.sk))
      .filter(this.itemNotUndefined) as T[]

  @enumerable(false)
  getParentEdges = (item: T): (T & EdgeInstanceType)[] =>
    Object.values(this.skMapped[item.sk] ?? {}).filter((child) => child && child.pk !== item.sk) as (T & EdgeInstanceType)[]

  @enumerable(false)
  getParentNodes = (item: T): T[] =>
    this.getParentEdges(item)
      .map((item) => this.getPrimaryItem(item.pk))
      .filter(this.itemNotUndefined) as T[]

  /** does the node or edge exist in this */
  @enumerable(false)
  itemExists = (item: T): boolean => {
    return !!this.skMapped[item.sk]?.[item.pk] && !!this.pkMapped[item.pk]?.[item.sk]
  }

  /** applies provided function to each handing node then to each successive parent node, providing the array of previous child results, used in aggregation up hierarchy */
  @enumerable(false)
  applyRecursivelyFromHangingNodes = async <R>(fn: (item: T, lowerResults: R[]) => Promise<R>, apexNode: T): Promise<R> => {
    const lowerResults = await Promise.all(this.getChildNodes(apexNode).map(async (item) => await this.applyRecursivelyFromHangingNodes(fn, item)))
    const result: R = await fn(apexNode as T, lowerResults)
    return result
  }

  /** used by getDecedents and getAscendents */
  @enumerable(false)
  iterate = ({ fn, items, maxIterations, iteration = 0 }: NodeMapperIterationRequest<T[]>) => {
    const combinedResults: T[] = []
    const iterationResult: T[] = fn(items)
    if (iterationResult.length > 0 && (!maxIterations || iteration < maxIterations)) {
      const nextResults = this.iterate({ fn, items: iterationResult, maxIterations, iteration: iteration + 1 })
      nextResults.forEach((r) => combinedResults.push(r))
    }
    iterationResult.forEach((r) => combinedResults.push(r))
    return combinedResults
  }

  /** returns decendent nodes not including initial items,
   * TODO add edgeTypeId filter
   */
  @enumerable(false)
  getDecendents = (items: T[], maxIterations?: number) =>
    this.iterate({
      fn: (items: T[]) => {
        const itemChildren = items
          .map((item) => this.getChildNodes(item))
          .reduce((a, cs) => {
            cs.forEach((c) => a.push(c))
            return a
          }, [] as T[])
        return itemChildren
      },
      items,
      maxIterations,
    })

  /** Returns ascendent nodes (not edges). Does not return initial items.
   * TODO add edgeTypeId filter
   */
  @enumerable(false)
  getAscendents = (items: T[], maxIterations?: number) =>
    this.iterate({
      fn: (items: T[]) => {
        const itemParents = items
          .map((item) => this.getParentNodes(item))
          .reduce((a, cs) => {
            cs.forEach((c) => a.push(c))
            return a
          }, [] as T[])
        return itemParents
      },
      items,
      maxIterations,
    })

  /** Returns the ascending nodes and edges from, but not including, startNode.
   * @param {T} startNode - The node to begin ascending from. Not included in result.
   * @param {string[]} [allowedEdgeTypes] - A list of edgeTypeIds that will be ascended. Defaults to any and all if undefined
   * The edgeTypeIdFilter is done by and 'includes' not an 'equals'
   * This differs from getAscendents in that edges are returned
   */
  @enumerable(false)
  getAscendingNodesAndEdges = (startNode: T, allowedEdgeTypes?: string[]) => {
    //let currentAscendingNode: T | undefined = startNode;
    const ascendingNodesAndEdges: T[] = []

    const seenNodes: string[] = [`${startNode.pk}${startNode.sk}`] // Prevent loops, which idk if they're possible
    const ascensionQueue: T[] = [startNode]

    do {
      const curNode = ascensionQueue.shift()
      if (!curNode) continue

      const parentEdges = this.getParentEdges(curNode).filter((edge) => !allowedEdgeTypes || allowedEdgeTypes.includes(edge.edgeTypeId))

      parentEdges.forEach((parentEdge) => {
        ascendingNodesAndEdges.push(parentEdge)
        const ascendantNode = this.getOtherNode(curNode, parentEdge)
        if (!ascendantNode) return
        const ascendantId = `${ascendantNode.pk}${ascendantNode.sk}`
        if (!seenNodes.find((sn) => sn === ascendantId)) {
          ascendingNodesAndEdges.push(ascendantNode)
          ascensionQueue.push(ascendantNode)
          seenNodes.push(ascendantId)
        }
      })
    } while (ascensionQueue.length > 0)

    return ascendingNodesAndEdges
  }

  /** returns array of nodes by type */
  @enumerable(false)
  getNodesOfType = (typeId: string): T[] => this.getContainedItems().filter((item) => item.type === typeId)

  /** returns map of node types with count */
  @enumerable(false)
  containedTypes = (): MapLike<number> => {
    const containedTypes: MapLike<number> = {}
    Object.keys(this.skMapped).forEach((k) => {
      const t = k.split(':')[0]
      if (!containedTypes[t]) {
        containedTypes[t] = 1
      } else {
        containedTypes[t]++
      }
    })
    return containedTypes
  }

  /** returns number of nodes, optionally filtered by type, may be expensive hence nodeCount, edgeCount */
  @enumerable(false)
  countNodes = (type?: string) => Object.keys(this.skMapped).filter((k) => !type || k.startsWith(type)).length

  /** returns number of edges and nodes */
  @enumerable(false)
  countItems = (type?: string) => Object.keys(this.skMapped).filter((k) => !type || k.startsWith(type)).length

  /**
   * returns random node or edge, primarily for testing
   */
  @enumerable(false)
  pickRandomOne = () => {
    const pickRandomEntry = (m: MapLike<any>) => {
      const keys = Object.keys(m)
      return m[keys[Math.floor(Math.random() * keys.length)]]
    }
    return pickRandomEntry(pickRandomEntry(this.skMapped))
  }

  @enumerable(false)
  pickRandomNode = () => {
    const nodes = this.getContainedItems()
    return nodes[Math.floor(Math.random() * nodes.length)]
  }

  @enumerable(false)
  pickRandomEdge = () => {
    const edges = this.getContainedEdges()
    return edges[Math.floor(Math.random() * edges.length)]
  }

  /** changes the id of a node and its references in any edge connected to it */
  @enumerable(false)
  changeIdentity = (item: T & { type?: string; id?: string }, newId: string) => {
    const replacementItems: T[] = []
    if (newId && isValidBusinessObject(item)) {
      let reKeyedItemsCount = 0,
        removedItemsCount = 0
      const reKeyedItem = reKeyItem(item, newId)
      reKeyedItemsCount++
      replacementItems.push(reKeyedItem)
      const childEdges = this.getChildEdges(item) // pk is item.pk, id is not item.id
      const parentEdges = this.getParentEdges(item) as (T & { id?: string })[] // sk is item.sk, id is item.id

      const reKeyedParentEdge = reKeyItem(item, newId!)

      parentEdges.forEach((item) => {
        if (item.id) {
          replacementItems.push(reKeyedParentEdge)
        }
        reKeyedItemsCount++
      })

      childEdges.forEach((edge) => {
        replacementItems.push({ ...edge, pk: reKeyedItem.sk })
        reKeyedItemsCount++
      })

      /** remove old items from nodeEdgeMapper */
      ;[item, ...childEdges, ...parentEdges].forEach((item) => {
        this.removeItem(item)
        removedItemsCount++
      })

      /** add new items to nodeEdgeMapper */
      this.loadItems(replacementItems)

      debug && console.log(`rekeyed ${reKeyedItemsCount}, removed ${removedItemsCount}, loaded ${replacementItems.length}`)
    } else {
      console.log('No id or not validBusinessObject')
    }
  }

  /** clone to the level of the SK PK maps, does not clone objects so be careful of intent */
  @enumerable(false)
  clone = () => {
    const clone = new NodeEdgeMapper<T>()
    clone.loadItems(this.getContainedNodesAndEdges())
    return clone
  }

  /** gets nodes and edges in this nodeEdgeMapper that are NOT in the comparable nodeEdgeMapper */
  @enumerable(false)
  getMissingNodesAndEdgesInOther = (comparable: NodeEdgeMapper<T>): MissingNodeAndEdges => {
    const thisNodesAndEdges = this.getContainedNodesAndEdges()

    const nodes: DatastoreObjectTypes = []
    const edges: DatastoreObjectTypes = []

    thisNodesAndEdges.forEach((item) => {
      if (!comparable.itemExists(item)) {
        if (isNode(item)) {
          nodes.push(item)
        }
        if (isEdge(item)) {
          edges.push(item)
        }
      }
    })
    return { nodes, edges }
  }

  @enumerable(false)
  getSummary = () => {
    const pseudos: T[] = []
    const isNotPseuodFilter = getRejectionBucketingFilter({filter: ( item: T) => !isPseudoAttribute( item ), rejectionsBucket: pseudos})
    const unterminatedEdges = this.getUnterminatedEdges().filter( isNotPseuodFilter )
    return {
      nodeCount: this.nodeCount,
      edgeCount: this.edgeCount,
      pseudoAttributes: pseudos.length,
      apexNodes: this.getApexNodes().length,
      unterminatedEdges: unterminatedEdges.length,
      orphanedNodes: this.getOrphanedNodes().length,
      byType: {...this.containedTypes(), ...summarizeCoreApplicationObjects( pseudos )},
    }
  }
}
